import React, { useEffect, useMemo } from "react";
import { Home } from "lucide-react";
import { useAppLauncherState } from "./useAppLauncherState.jsx";
import { usePlatformContext } from "./PlatformContext.jsx";
import { getLauncherIconForApp, resolveAppIconKey } from "./launcherIcons.js";
import { normalizeGovernanceAppKey } from "@tennis-matches/services/governance";
function read(t, key, fallback = "") {
  const v = typeof t === "function" ? t(key) : key;
  const result = v && v !== key ? v : fallback || key || "";
  return result == null ? "" : String(result);
}

function appLabel(item, t) {
  const titleKey = String(item?.titleKey || "").trim();
  if (titleKey && typeof t === "function") {
    const tr = t(titleKey);
    if (tr && tr !== titleKey) return String(tr);
  }
  return String(item?.title || item?.appKey || item?.id || "").trim();
}

function appKeyNormalized(item) {
  const k = String(item?.appKey || item?.id || "").trim();
  return normalizeGovernanceAppKey(k);
}

export function AppLauncherContent({
  t,
  onSelectApp,
  onShowAllApps,
  onDiscoverApps,
  launcherAccess,
  onGoHome,
  homeMode = false,
  // Für spätere Varianten (Marktplatz zeigt alle Apps): Zugriff vs. Sichtbarkeit.
  showDiscoverApps = false,
  className = "",
}) {
  const { activeAppKey } = usePlatformContext();
  const { launcherApps, allApps, openApp } = useAppLauncherState({
    accessReady: launcherAccess != null && launcherAccess.accessReady !== false,
    isSuperAdmin: launcherAccess?.isSuperAdmin === true,
    allowedAppKeys: launcherAccess?.allowedAppKeys,
  });

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const launcherKeys = (launcherApps || [])
      .map((a) => appKeyNormalized(a))
      .filter(Boolean);
    const allKeys = (allApps || [])
      .map((a) => appKeyNormalized(a))
      .filter(Boolean);
    // eslint-disable-next-line no-console
    console.debug("[tm-access-diag] AppLauncherContent", {
      renderedLauncherAppKeys: launcherKeys,
      renderedAllAppKeys: allKeys,
    });
  }, [launcherApps, allApps]);

  const displayApps = useMemo(
    () => (Array.isArray(launcherApps) ? launcherApps : []),
    [launcherApps],
  );
  const showAllLabel = read(t, "platform.launcher.showAllApps");
  const allAppsTitle = read(t, "platform.launcher.allApps");
  const discoverTitle = read(t, "platform.launcher.discoverApps.title");
  const discoverHint = read(t, "platform.launcher.discoverApps.hint");
  const discoverCta = read(t, "platform.launcher.discoverApps.cta");
  const homeLabel = read(t, "platform.launcher.home");
  const isSuperAdmin = launcherAccess?.isSuperAdmin === true;
  const canDiscoverApps =
    showDiscoverApps && !isSuperAdmin && typeof onDiscoverApps === "function";
  const showHomeButton = typeof onGoHome === "function" && !homeMode;
  const rootClassName = `platformAppLauncherContent${className ? ` ${className}` : ""}`;

  return (
    <div className={rootClassName}>
      {showHomeButton ? (
        <div className="platformAppLauncherContent__homeRow">
          <button
            type="button"
            className="platformAppLauncherContent__homeBtn"
            onClick={() => onGoHome()}
          >
            <Home size={16} strokeWidth={2} aria-hidden="true" />
            <span>{homeLabel}</span>
          </button>
        </div>
      ) : null}
      {displayApps.length > 0 ? (
        <section className="platformAppLauncherContent__section">
          <div className="platformAppLauncherContent__sectionHead">
            <h3 className="platformAppLauncherContent__title">
              {allAppsTitle}
            </h3>
            {typeof onShowAllApps === "function" ? (
              <button
                type="button"
                className="platformAppLauncherContent__showAllBtn"
                onClick={() => {
                  onShowAllApps();
                }}
              >
                {showAllLabel}
              </button>
            ) : null}
          </div>
          <div className="platformAppLauncherContent__grid" role="list">
            {displayApps.map((app) => {
              const key = String(app?.appKey || app?.id || "").trim();
              if (!key) return null;
              const label = appLabel(app, t);
              const isActive = activeAppKey && activeAppKey === key;
              const Icon = getLauncherIconForApp(app);
              const iconKey = resolveAppIconKey(app);
              return (
                <button
                  key={`launcher-app-${key}`}
                  type="button"
                  className={`platformAppLauncherContent__appCard${isActive ? " is-active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => {
                    openApp(app);
                    if (typeof onSelectApp === "function") onSelectApp(app);
                  }}
                >
                  <span
                    className="platformAppLauncherContent__appIcon"
                    aria-hidden="true"
                    data-icon-key={iconKey}
                  >
                    {Icon ? (
                      <Icon size={16} strokeWidth={2} />
                    ) : (
                      <span className="platformAppLauncherContent__appIconFallback">
                        {String(iconKey || label || "?")
                          .trim()
                          .slice(0, 1)
                          .toUpperCase()}
                      </span>
                    )}
                  </span>
                  <span className="platformAppLauncherContent__appLabel">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {canDiscoverApps ? (
        <section
          className="platformAppLauncherContent__discover"
          aria-label={discoverTitle}
        >
          <p className="platformAppLauncherContent__discoverHint">
            {discoverHint}
          </p>
          <button
            type="button"
            className="platformAppLauncherContent__discoverBtn"
            onClick={() => {
              onDiscoverApps();
            }}
          >
            {discoverCta}
          </button>
        </section>
      ) : null}
    </div>
  );
}
